import { formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { formatSeconds } from './format';

/*
 * Clarity device split: enhet · økter (share bar) · scrolledybde (0–100 bar)
 * · aktiv tid. Defensive: every numeric field tolerates null → em dash, bars
 * collapse to 0 width. Device names from Clarity ('Mobile'/'Tablet'/'PC'/…) are
 * localized case-insensitively, unknown values render raw.
 */

export interface DeviceRow {
  device: string;
  sessions: number | null;
  scrollDepthPct: number | null;
  activeTimeSec: number | null;
}

const MESSAGES = {
  nb: {
    device: 'Enhet',
    sessions: 'Økter',
    scroll: 'Scrolledybde',
    active: 'Aktiv tid',
    names: {
      mobile: 'Mobil',
      tablet: 'Nettbrett',
      pc: 'Desktop',
      desktop: 'Desktop',
      other: 'Annet',
    } as Record<string, string>,
  },
  en: {
    device: 'Device',
    sessions: 'Sessions',
    scroll: 'Scroll depth',
    active: 'Active time',
    names: {
      mobile: 'Mobile',
      tablet: 'Tablet',
      pc: 'Desktop',
      desktop: 'Desktop',
      other: 'Other',
    } as Record<string, string>,
  },
};

function clampPct(v: number | null): number {
  if (v == null || !Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, v));
}

export function DeviceTable({ rows }: { rows: DeviceRow[] }) {
  const m = useViewMessages(MESSAGES);
  const totalSessions = rows.reduce((sum, r) => sum + (r.sessions ?? 0), 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[20rem] text-body">
        <thead>
          <tr className="border-b border-border text-label text-fg-subtle">
            <th className="sticky left-0 z-10 bg-surface py-1.5 text-left font-medium">
              {m.device}
            </th>
            <th className="py-1.5 text-right font-medium">{m.sessions}</th>
            <th className="py-1.5 text-right font-medium">{m.scroll}</th>
            <th className="py-1.5 text-right font-medium">{m.active}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const share =
              totalSessions > 0 && r.sessions != null ? (r.sessions / totalSessions) * 100 : 0;
            return (
              <tr key={r.device} className="border-b border-border/60 last:border-0">
                <td className="sticky left-0 z-10 bg-surface py-2 text-fg">
                  {m.names[r.device.toLowerCase()] ?? r.device}
                </td>
                <td className="py-2">
                  <div className="flex items-center justify-end gap-2">
                    <div className="h-1.5 w-12 overflow-hidden rounded-full bg-surface-3 sm:w-20">
                      <div
                        className="h-full rounded-full bg-accent-click"
                        style={{ width: `${clampPct(share)}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-fg-muted tabular">
                      {formatMetric(r.sessions, 'int')}
                    </span>
                  </div>
                </td>
                <td className="py-2">
                  <div className="flex items-center justify-end gap-2">
                    <div className="h-1.5 w-10 overflow-hidden rounded-full bg-surface-3 sm:w-16">
                      <div
                        className="h-full rounded-full bg-accent-lead"
                        style={{ width: `${clampPct(r.scrollDepthPct)}%` }}
                      />
                    </div>
                    <span className="w-14 text-right text-fg-muted tabular">
                      {formatMetric(r.scrollDepthPct, 'pct')}
                    </span>
                  </div>
                </td>
                <td className="py-2 text-right text-fg-subtle tabular">
                  {formatSeconds(r.activeTimeSec)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
